import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Copy01Icon } from '@untitledui-icons/react/line'

import { Button } from '@/components/base/buttons'
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@/components/ui/Modal'
import type { WidgetOverviewRow } from '@/hooks/api/useWidgets'
import { createTriggerSnippet } from './widgetConfig.preview'

interface SnippetBlockProps {
  title: string
  description: string
  snippet: string
  notes?: string[]
}

function SnippetBlock({ title, description, snippet, notes = [] }: SnippetBlockProps) {
  const { t } = useTranslation()

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet)
      toast.success(t('resources.widgets.embed.copied_toast'))
    } catch {
      toast.error(t('resources.widgets.embed.copy_failed_toast'))
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="space-y-0.5">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{title}</p>
          <p className="text-xs text-gray-600 dark:text-gray-400">{description}</p>
        </div>
        <Button
          type="button"
          color="secondary"
          size="sm"
          iconLeading={Copy01Icon}
          onPress={handleCopy}
        >
          {t('resources.generic.copy')}
        </Button>
      </div>

      <textarea
        readOnly
        spellCheck={false}
        value={snippet}
        rows={Math.max(3, snippet.split('\n').length)}
        onFocus={(e) => e.target.select()}
        aria-label={t('resources.widgets.embed_modal.aria_label')}
        className="focus:border-brand-500 focus:ring-brand-500 w-full resize-y rounded-lg border border-gray-200 bg-gray-50 p-3 font-mono text-xs text-gray-700 focus:ring-1 focus:outline-none dark:border-gray-700 dark:bg-gray-950 dark:text-gray-300"
      />

      {notes.map((note) => (
        <p key={note} className="text-xs text-gray-500 dark:text-gray-400">
          {note}
        </p>
      ))}
    </div>
  )
}

interface WidgetEmbedModalProps {
  row: WidgetOverviewRow | null
  onClose: () => void
}

/**
 * What one assistant pastes on its website. The loader script is the
 * personalised part (its widget key resolves the number server-side); the host
 * page trigger is optional, and only carries a venue when the template shows a
 * picker.
 */
export function WidgetEmbedModal({ row, onClose }: WidgetEmbedModalProps) {
  const { t } = useTranslation()
  const base = 'resources.widgets.embed_modal'

  const script = row?.embed_snippet ?? null
  const venueId = row?.template_kind === 'multi' ? String(row.assistant_id) : null
  const trigger = createTriggerSnippet(venueId)
  const triggerNotes = [
    t(`${base}.style_note`),
    ...(venueId ? [t(`${base}.venue_note`, { assistant: row?.assistant_name })] : []),
  ]

  return (
    <Modal isOpen={!!row} onOpenChange={(isOpen) => !isOpen && onClose()} size="2xl">
      <ModalContent>
        <ModalHeader onClose={onClose} bordered={false}>
          <div className="space-y-1">
            <ModalTitle>{t(`${base}.title`, { assistant: row?.assistant_name ?? '' })}</ModalTitle>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t(`${base}.subtitle_floating`)}
            </p>
          </div>
        </ModalHeader>

        <ModalBody className="max-h-[70vh] space-y-5 overflow-y-auto">
          {!script ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t(`${base}.needs_template`)}
            </p>
          ) : (
            <>
              <SnippetBlock
                title={t(`${base}.script_title`)}
                description={t(`${base}.script_description`)}
                snippet={script}
              />
              <SnippetBlock
                title={t(`${base}.trigger_title`)}
                description={t(`${base}.trigger_description`)}
                snippet={trigger}
                notes={triggerNotes}
              />
            </>
          )}
        </ModalBody>

        <ModalFooter bordered={false}>
          <Button color="secondary" size="md" onPress={onClose}>
            {t('resources.generic.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
